// Sidebar.tsx
import React from 'react';
import {
    Box, Button, Typography, Divider, Paper, IconButton, Chip, CircularProgress
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { StyledDialog, StyledDialogTitle } from '../../main_components/material_table/styles/general_form_styles';

import SMIT_QR from "../../imgComponents/iconos/qr_smit.png";

import { bot_telegram_post } from '../../services/supports/supports';



const Sidebar: React.FC = () => {
    const [open_form, setOpenForm] = React.useState(false);
    const [codigoGenerado, setCodigoGenerado] = React.useState<string | null>(null);
    const [loadingCode, setLoadingCode] = React.useState(false);

    const handleClose = () => {
        setOpenForm(false);
        setCodigoGenerado(null);
        setLoadingCode(false);
    };

    const generarCodigo = async () => {
        setLoadingCode(true);
        setCodigoGenerado(null); // limpia anterior

        try {
            // Hacer POST y extraer directamente el código
            const response = await bot_telegram_post();

            if (response && response.data?.code) {
                setCodigoGenerado(response.data.code);
            } else {
                console.warn("No se recibió un código válido desde el servidor.");
            }

        } catch (error) {
            console.error("Error al generar código Telegram:", error);
        } finally {
            setLoadingCode(false);
        }
    };


    return (
        <Box p={2}>
            {/* Modal Telegram */}
            <StyledDialog open={open_form} maxWidth="md" fullWidth>
                <StyledDialogTitle>
                    <Typography variant="subtitle1" fontWeight={700}>
                        <Chip label="QR" size="small" sx={{ mr: 1, backgroundColor: '#E0E7FF', color: '#3730A3' }} />
                        <span style={{ color: 'white' }}>Bot de Telegram</span>
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </StyledDialogTitle>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        p: 1,
                        backgroundColor: '#F9FAFB',
                        justifyContent: 'center',
                    }}
                >
                    {/* Columna QR */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 300,
                            maxWidth: 400,
                            backgroundColor: '#ffffff',
                            borderRadius: 3,
                            p: 3,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        }}
                    >
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            Código QR
                        </Typography>
                        <Box
                            component="img"
                            src={SMIT_QR}
                            alt="QR Telegram"
                            sx={{
                                width: '100%',
                                maxWidth: 220,
                                borderRadius: 2,
                                mx: 'auto',
                                display: 'block',
                                my: 2
                            }}
                        />
                        <Box mt={2}>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Características del Bot
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={1}>
                                <Chip label="Comunicación Directa" icon={<span>💬</span>} sx={{ backgroundColor: '#blue' }} />
                                <Chip label="Respuestas Rápidas" icon={<span>⚡</span>} sx={{ backgroundColor: '#BBF7D0' }} />
                                <Chip label="Acceso Móvil" icon={<span>📱</span>} sx={{ backgroundColor: '#E9D5FF' }} />
                            </Box>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            href="https://t.me/OD2_SMIT_TELEGRAM_BOT"
                            target="_blank"
                            startIcon={<span>↗</span>}
                            sx={{
                                backgroundColor: '#287ad2',
                                color: '#fff',
                                mt: 2,
                                fontWeight: 'bold',
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: '#287ad2',
                                },
                            }}
                        >
                            Acceder Directamente
                        </Button>

                        <Box mt={3} p={2} sx={{ backgroundColor: '#F1F5F9', borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                ¿No puedes escanear el QR?
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Copia este enlace en tu navegador:
                            </Typography>
                            <Box
                                sx={{
                                    mt: 1,
                                    backgroundColor: '#fff',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    p: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography variant="body2" sx={{ fontSize: '13px', wordBreak: 'break-all' }}>
                                    https://t.me/OD2_SMIT_TELEGRAM_BOT
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => navigator.clipboard.writeText("https://t.me/OD2_SMIT_TELEGRAM_BOT")}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    {/* Columna instrucciones */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 300,
                            maxWidth: 400,
                            backgroundColor: '#F0F9FF',
                            borderRadius: 3,
                            p: 3,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            <span role="img" aria-label="info">ℹ️</span> Instrucciones de Uso
                        </Typography>
                        <Typography variant="body2" mb={2}>
                            Sigue estos pasos para comenzar a usar el bot:
                        </Typography>

                        {[
                            ["Escanea el código QR", "Usa la cámara de tu teléfono o una app de QR"],
                            ["Abre Telegram", "Se abrirá automáticamente la aplicación de Telegram"],
                            ["Inicia la conversación", 'Presiona "Iniciar" o envía /start al bot'],
                            ["¡Listo para usar!", "Sigue las instrucciones del bot"],
                            ["Genera tu código", "Haz clic para generar tu código de acceso dinámico"]
                        ].map(([title, desc], i) => (
                            <Box
                                key={i}
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 2,
                                    p: 2,
                                    mb: 1,
                                    borderLeft: `4px solid ${['#818CF8', '#38BDF8', '#F472B6', '#34D399', '#6366F1'][i]}`,
                                }}
                            >
                                <Typography fontWeight={600} fontSize="14px" gutterBottom>
                                    {i + 1}. {title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {desc}
                                </Typography>
                            </Box>
                        ))}

                        {/* Botón para generar código */}
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={loadingCode}
                            onClick={generarCodigo}
                            sx={{
                                mt: 2,
                                backgroundColor: '#287ad2',
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: '#287ad2',
                                },
                            }}
                        >
                            {loadingCode ? <CircularProgress size={20} color="inherit" /> : 'Generar Código'}
                        </Button>

                        {/* Mostrar código generado */}
                        {codigoGenerado && (
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    p: 2,
                                    mt: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    border: '1px solid #E5E7EB',
                                    fontFamily: 'monospace',
                                }}
                            >
                                <Typography fontSize={14} fontWeight={600}>
                                    {codigoGenerado}
                                </Typography>
                                <IconButton
                                    onClick={() => navigator.clipboard.writeText(codigoGenerado)}
                                    size="small"
                                    title="Copiar código"
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Box>

            </StyledDialog>


            {/* Panel lateral */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    backgroundColor: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                }}
            >
                <Typography variant="h6" fontWeight="bold">
                    SIMA
                </Typography>

                <Divider sx={{ my: 2, width: '100%', borderColor: '#E5E7EB' }} />
                <Typography variant="subtitle2" fontWeight="bold">
                    Teléfonos de Soporte:
                </Typography>

                {[
                    "272-126-4935",
                    "272-122-8112",
                    "272-172-2523",
                    "272-179-4068",
                    "272-167-2277",
                    "272-723-0084",
                    "272-703-3141"
                ].map((numero, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            mb: 0.5,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {numero}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(numero)}
                            title="Copiar número"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}

                <Divider sx={{ my: 2, width: '100%', borderColor: '#E5E7EB' }} />


                <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
                    Correos de Soporte:
                </Typography>


                {["soporte@herramientasinformaticas.com",].map((email, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                            width: '100%',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                maxWidth: '75%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={email}
                        >
                            {email}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(email)}
                            title="Copiar correo"
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}


                <Divider sx={{ my: 2, width: '100%', borderColor: '#E5E7EB' }} />

                <Box>
                    <Button
                        variant="contained"
                        startIcon={<HelpOutlineIcon />}
                        sx={{
                            backgroundColor: '#0472e9',
                            textTransform: 'none',
                            borderRadius: 2,
                            fontWeight: 600,
                            px: 3,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                        onClick={() => setOpenForm(true)}
                    >
                        Vincular a Telegram
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default Sidebar;



